/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { faChevronLeft, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  QUESTION_VALUE_TYPE,
  QUESTION_FILTER_TYPE,
  AUTO_SAVING_TIME,
  ONBOARDING_STEPS,
  COLOR_SCHEME_ONBOARDING_STEPS,
  OPEN_TEXT_ONBOARDING_STEPS,
  QUESTION_TYPE,
} from '../../../../constants/common';
import { SurveyThemeNulled, surveyWizardThemes } from '../../../../constants/themes';
import FilterCategory from './filter.category';
import DataBinning from './data.binning';
import types from '../../../../types';
import {
  updateSurveySampleById,
  autoSavedSurveySample,
  autoSavingSurveySample,
  setAutoSavedPending,
} from '../../../../actions/survey.sample.action';
import './styles.css';
import SampleDimension404Dialog from './sample.dimension.404.dialog';
import AnswerList from './answer.list';
import LabelList from './label.list';

import validateOnboardingStep from '../../../../constants/validateOnboardingStep';
import benTooltip from '../../../../Components/BenTooltip';
import { postOnBoardingProcess } from '../../../../actions/user.action';

const SurveySTypeQuestionEditForm = ({ handleEditMode }) => {
  const dispatch = useDispatch();
  const storeJwt = useSelector((state) => state.session.jwt);
  const [selectedTheme, setSelectedTheme] = useState();
  const [prompt, setPrompt] = useState('');
  const [labels, setLabels] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [binningConfiguration, setBinningConfiguration] = useState();
  const [filterType, setFilterType] = useState('');
  const { onBoarding } = useSelector((state) => state.users);
  const [steps, setSteps] = useState(ONBOARDING_STEPS);
  const [hiddenAnswers, setHiddenAnswers] = useState([]);
  const [answerIdOrders, setAnswerIdOrders] = useState([]);
  const [labelIdOrders, setLabelIdOrders] = useState([]);
  const {
    sample,
    dimensions,
    error: responseError,
  } = useSelector((state) => state.surveySample);

  const {
    autoSaving,
    autoSavingPending,
    autoSaved,
  } = useSelector((state) => state.autoSaving);

  const { visualizations, surveyLocationFilter } = useSelector((state) => state.surveyDetails);

  const onQuestionPromtChange = (e) => {
    setPrompt(e.target.value);
  };

  const onAnswerChange = (e, index) => {
    const answersToUpdate = [...answers];
    answersToUpdate[index].value = e.target.value;
    setAnswers([...answersToUpdate]);
  };

  const onHiddeChange = (hiddenList) => {
    setHiddenAnswers(hiddenList);
  }

  const onAnswerIdOrderChange = (idOrders) => {
    setAnswerIdOrders([...idOrders]);
  }
  const onLabelIdOrderChange = (labelOrder) => {
    setLabelIdOrders([...labelOrder]);
  }

  const onBinningConfiguration = (value) => {
    dispatch(setAutoSavedPending());
    setBinningConfiguration(value);
  }

  const onLabelChange = (e, index) => {
    const labelsToUpdate = [...labels];
    labelsToUpdate[index].value = e.target.value;
    setLabels([...labelsToUpdate]);
  };

  useEffect(() => {
    if (!sample || !dimensions) {
      setSteps(null);
      return;
    }
    const text = [QUESTION_VALUE_TYPE.MULTIPLE, QUESTION_VALUE_TYPE.GRID].includes(
      sample?.Data.valueType
    )
      ? sample.Data.text
      : sample.Data.label || sample.Data.text;

    setPrompt(text);
    setFilterType(sample.Data.filterType);

    if (sample?.Data.valueType !== QUESTION_VALUE_TYPE.MULTIPLE) {
      const { allowedValues } = dimensions[0].Data;
      const answerList =
        Object.keys(allowedValues || [])
          .filter((key) => key >= 0)
          .map((key) => ({ id: key, value: allowedValues[key] }));
      setAnswers(answerList);

      setHiddenAnswers(sample.Data.hiddenAnswers || []);
      const answerIds = answerList.map((answer) => (answer.id));
      const idOrders = sample.Data.answerOrders && sample.Data.answerOrders.length > 0 ? sample.Data.answerOrders : answerIds;
      setAnswerIdOrders(idOrders);
    }
    if (QUESTION_VALUE_TYPE.OPEN_NUMERIC === sample?.Data.valueType) {
      setBinningConfiguration({
        ...dimensions[0].Data.binningConfiguration,
        label: dimensions[0].Data.description,
      });
    }
    if ([QUESTION_VALUE_TYPE.MULTIPLE, QUESTION_VALUE_TYPE.GRID].includes(sample?.Data.valueType)) {
      const labelList = dimensions.filter(dimension => dimension.Data.valueType !== QUESTION_VALUE_TYPE.OPEN_TEXT)
        .map((dimension) => ({
          id: dimension.Id,
          value: dimension.Data.label,
        }));
      const currentOrderId = labelList.map((label) => label.id);
      const labelOrders = sample.Data.labelOrders && sample.Data.labelOrders.length > 0 ? sample.Data.labelOrders : currentOrderId;
      setLabels(labelList);
      setLabelIdOrders(labelOrders);
    }
    if ([QUESTION_VALUE_TYPE.OPEN_TEXT].includes(sample?.Data.valueType)) {
      setSteps(OPEN_TEXT_ONBOARDING_STEPS);
    } else if (!(filterType !== QUESTION_FILTER_TYPE.ATTRIBUTE &&
      [QUESTION_VALUE_TYPE.SINGLE, QUESTION_VALUE_TYPE.GRID].includes(
        sample?.Data.valueType
      ))) {
      setSteps(COLOR_SCHEME_ONBOARDING_STEPS);
    }
    else {
      setSteps(ONBOARDING_STEPS);
    }
  }, [sample, filterType, dimensions]);

  const updateDimensions = useCallback(() => {
    if (!dimensions) return;
    if (labels.length === dimensions.length) {
      labels.forEach((label, index) => {
        answers.forEach((an) => {
          dimensions[index].Data.allowedValues[an.id] = an.value;
        });
        dimensions[index].Data.label = label.value;
        dimensions[index].Data.text = prompt;
      });
    } else {
      const newAnswer = answers.reduce((accumulator, currentItem) => {
        accumulator[currentItem.id] = currentItem.value;
        return accumulator;
      }, {});
      for (const prop in dimensions[0].Data.allowedValues) {
        if (Number(prop) < 0) {
          newAnswer[prop] = dimensions[0].Data.allowedValues[prop];
        }
      }

      dimensions[0].Data.allowedValues = newAnswer;
      if (QUESTION_VALUE_TYPE.OPEN_TEXT === sample?.Data.valueType) {
        dimensions[0].Data.label = prompt;
        dimensions[0].Data.text = prompt;
      }
    }
  }, [answers, sample, dimensions, labels, prompt]);

  const updateSampleAndDimensions = (theme = null, _filterType = null) => {
    updateDimensions();
    sample.Data.label = prompt;
    sample.Data.text = prompt;
    sample.Data.filterType = _filterType || filterType;

    dispatch({
      type: types.reducerTypes.SURVEY_SAMPLE_DIMENSION_GET_SUCCESS,
      payload: {
        sample,
        dimensions,
        sampleTheme: theme,
      },
    });
  };

  const onSave = useCallback(() => {
    if (!sample) return;
    const { Id } = sample;
    if (QUESTION_VALUE_TYPE.OPEN_NUMERIC === sample?.Data.valueType) {
      const { label, ...binningConfig } = binningConfiguration
      dimensions[0].Data.binningConfiguration = binningConfig;
      dimensions[0].Data.description = label;
    }

    updateDimensions();
    dispatch(
      updateSurveySampleById(
        Id,
        {
          Id,
          Data: {
            label: prompt,
            text: prompt,
            filterType,
            hiddenAnswers,
            answerOrders: answerIdOrders,
            labelOrders: labelIdOrders,
            questionType: QUESTION_TYPE[selectedTheme?.index],
          },
        },
        dimensions,
        surveyLocationFilter,
        storeJwt,
        sample.Parent
      )
    );
    dispatch(autoSavedSurveySample());
  }, [
    dispatch,
    storeJwt,
    answerIdOrders,
    hiddenAnswers,
    dimensions,
    filterType,
    selectedTheme,
    labelIdOrders,
    prompt,
    updateDimensions,
    sample,
    binningConfiguration,
    surveyLocationFilter,
  ]);

  const onChangeTheme = (theme) => {
    dispatch(setAutoSavedPending());
    setSelectedTheme(theme);
  };

  const onFilterTypeChange = (e) => {
    dispatch(setAutoSavedPending());
    setFilterType(e.target.value);
    if (e.target.value === QUESTION_FILTER_TYPE.ATTRIBUTE) {
      updateSampleAndDimensions(SurveyThemeNulled, e.target.value);
    } else {
      updateSampleAndDimensions(selectedTheme, e.target.value);
    }
  };

  useEffect(() => {
    if (!sample || !visualizations || visualizations.length <= 0) return;
    const themeIndex = QUESTION_TYPE.indexOf(sample?.Data.questionType);
    const currentThemes = surveyWizardThemes[themeIndex] || surveyWizardThemes[visualizations.details.scheme.index];
    setSelectedTheme(currentThemes);
  }, [sample, visualizations]);

  useEffect(() => {
    if ((!autoSaved && autoSaving && responseError) || !autoSavingPending) return;
    setTimeout(() => {
      dispatch(autoSavingSurveySample());
    }, AUTO_SAVING_TIME);
  }, [dispatch, autoSaved, autoSaving, autoSavingPending, responseError]);

  useEffect(() => {
    if ((!autoSaving && autoSaved)) return;
    if (responseError) return;
    onSave();
  }, [autoSaved, autoSaving, onSave, responseError]);

  useEffect(() => {
    if (!responseError) return;
    handleEditMode(null, 'sections');
    SampleDimension404Dialog(dispatch);
  }, [responseError, handleEditMode, dispatch]);

  const saveStepHandler = (step) => {
    onBoarding.push(step.value);
    dispatch(postOnBoardingProcess({ steps: onBoarding }, storeJwt))
  }

  return (
    <Col md="3" className="p-3 survey-sidebar edit-mode">
      <span
        className="d-flex"
        onClick={() => handleEditMode(null, 'sections')}
        onKeyDown={() => handleEditMode(null, 'sections')}
        role="button"
        tabIndex={0}
      >
        <FontAwesomeIcon size="sm" className="mr-3" icon={faChevronLeft} />
        Back to overview
      </span>
      <Col className="info-edit-form edit-body">
        {steps && <OverlayTrigger
          placement="right"
          defaultShow={validateOnboardingStep(steps.step3.value, steps.step3.previous, onBoarding)}
          show={validateOnboardingStep(steps.step3.value, steps.step3.previous, onBoarding)}
          overlay={(props) => benTooltip({
            ...props,
            title: steps.step3.title,
            content: steps.step3.content,
            close: () => saveStepHandler(steps.step3)
          })}>
          <div>
            <h4>Question Text</h4>
            <Form.Control
              as="textarea"
              className="mt-3"
              value={prompt}
              onChange={onQuestionPromtChange}
              onBlur={() => dispatch(setAutoSavedPending())}
              style={{ height: '100px' }}
              maxLength={500}
              placeholder="Survey Description"
            />
          </div>
        </OverlayTrigger>}
        {QUESTION_VALUE_TYPE.OPEN_NUMERIC === sample?.Data.valueType && (
          <DataBinning
            binningConfiguration={binningConfiguration}
            onBinningConfigurationChange={(e) => onBinningConfiguration(e)}
          />)}

  {/* Answers */ }
  <AnswerList
    sample={sample}
    answers={answers}
    hiddenAnswers={hiddenAnswers}
    answerIdOrders={answerIdOrders}
    onAnswerIdOrderChange={onAnswerIdOrderChange}
    onAnswerChange={onAnswerChange}
    onHiddeChange={onHiddeChange}
  />

  {
    [QUESTION_VALUE_TYPE.MULTIPLE, QUESTION_VALUE_TYPE.GRID].includes(
      sample?.Data.valueType
    ) && labels.length > 0 && (
      <LabelList
        sample={sample}
        labels={labels}
        labelIdOrders={labelIdOrders}
        onLabelIdOrderChange={onLabelIdOrderChange}
        onLabelChange={onLabelChange}
      />
    )
  }
  {
    sample &&
    ![
      QUESTION_VALUE_TYPE.GRID_MULTIPLE,
      QUESTION_VALUE_TYPE.RANK_ORDER,
      QUESTION_VALUE_TYPE.OPEN_TEXT,
    ].includes(sample?.Data.valueType) && (
      <FilterCategory
        filterType={filterType}
        onFilterTypeChange={onFilterTypeChange}
        sample={sample}
        steps={steps}
      />
    )
  }
  {
    sample &&
    filterType !== QUESTION_FILTER_TYPE.ATTRIBUTE &&
    [QUESTION_VALUE_TYPE.SINGLE, QUESTION_VALUE_TYPE.GRID].includes(
      sample?.Data.valueType
    ) && (
      <>
        <h4 className="mb-2">
          Select Color Scheme
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                Gradient: For data in an ordered series (like increasing lengths of time)
                <br />
                Discrete: For data with no inherent order or rank (like school subjects)
                <br />
                Positive: For ordered data with two ends and a middle (like Likert scale
                <br />
                questions)
              </Tooltip>
            }
            delay={100}
          >
            <FontAwesomeIcon className="cursor-pointer ml-2" size="1x" icon={faInfoCircle} />
          </OverlayTrigger>
        </h4>
        {steps && <OverlayTrigger
          placement="right"
          defaultShow={validateOnboardingStep(steps?.step5?.value, steps?.step5?.previous, onBoarding)}
          show={validateOnboardingStep(steps?.step5?.value, steps?.step5?.previous, onBoarding)}
          overlay={(props) => benTooltip({
            ...props,
            title: steps?.step5?.title,
            content: steps?.step5?.content,
            close: () => saveStepHandler(steps.step5)
          })}>
          <div>
            {surveyWizardThemes.map((theme) => (
              <div className="mb-2" key={`theme-${theme.index}`}>
                <div
                  className={`colors-rounded ${theme.index === selectedTheme?.index ? 'selected' : ''
                    }`}
                  onClick={() => onChangeTheme(theme)}
                  role="button"
                  style={{ display: 'flex' }}
                >
                  {theme.scheme.map((color) => (
                    <div
                      role="button"
                      key={`color-${color}`}
                      style={{ backgroundColor: color, flex: 1, height: 20 }}
                    />
                  ))}
                </div>
                <span>{theme.label === 'Blue' ? 'Positive/Negative' : theme.label}</span>
              </div>
            ))}
          </div>
        </OverlayTrigger>}
      </>
    )
  }
      </Col >
  { steps && <OverlayTrigger
    placement="right"
    defaultShow={validateOnboardingStep(steps.step6.value, steps.step6.previous, onBoarding)}
    show={validateOnboardingStep(steps.step6.value, steps.step6.previous, onBoarding)}
    overlay={(props) => benTooltip({
      ...props,
      title: steps.step6.title,
      content: steps.step6.content,
      close: () => saveStepHandler(steps.step6)
    })}>
    <Button variant="primary" onClick={onSave} className="btn btn-custom dark mt-3">
      Save
    </Button>
  </OverlayTrigger>}
    </Col >
  );
};

SurveySTypeQuestionEditForm.propTypes = {
  handleEditMode: PropTypes.func.isRequired,
};

export default SurveySTypeQuestionEditForm;
